import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useT } from '../i18n';
import { Spinner } from './ui';

/**
 * Plays audio that needs our auth header: the file is fetched as a blob and
 * handed to <audio> as an object URL (initData never goes into a URL).
 */
export function AudioPlayer({
  queryKey,
  load,
}: {
  queryKey: unknown[];
  load: () => Promise<Blob>;
}) {
  const t = useT();
  const q = useQuery({ queryKey, queryFn: load, staleTime: Infinity, gcTime: 0, retry: 1 });
  const url = useMemo(() => (q.data ? URL.createObjectURL(q.data) : null), [q.data]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);

  if (q.isLoading) {
    return (
      <div className="flex justify-center py-3">
        <Spinner />
      </div>
    );
  }
  if (q.isError || !url) return <p className="text-sm text-hint">{t.audio.loadFailed}</p>;
  return <audio controls preload="auto" src={url} className="w-full" />;
}

/**
 * A take by its signed link to the light copy: <audio> streams it and starts
 * before the file is all there — no waiting for a whole WAV on a slow link. Every
 * refetch signs a new link to the same file: the first one is kept (it lives 6 hours),
 * so playback is never restarted; mount it with `key` = the take.
 */
export function LinkAudioPlayer({ src }: { src: string }) {
  const [first] = useState(src);
  return <audio controls preload="metadata" src={first} className="w-full" />;
}
